<template>

    <FloatingPanel title="Afbeelding" :width="400" :saveable="false">

        <div class="galaxy::common-canvas-blocks-utils-image-BlockPanel">

            <div class="tw-m-5">

                <div>

                    <div class="tw-mb-1 tw-text-gray-600 label">Nieuwe afbeelding uploaden?</div>

                    <label
                        ref="dropzone"
                        class="tw-border-dashed tw-border-gray-400 tw-h-32 tw-bg-gray-50 hover:tw-bg-gray-200 tw-transition tw-text-gray-500 hover:tw-text-gray-600 tw-text-center tw-text-sm tw-flex tw-cursor-pointer tw-p-0 dropzone"
                        :class="{ 'success': uploadSuccess }"
                        v-on:drop="onDrop"
                        v-on:dragover="allowDrop"
                        @click="uploadCancellationToken ? cancelUpload($event) : null">

                        <div class="tw-m-auto tw-px-2">

                            <template v-if="uploadSuccess">

                                <i class="fa fa-check-circle tw-text-2xl text-success"></i>
                                <div class="tw-mt-2 tw-text-gray-600">
                                    Afbeelding succesvol geüpload.
                                </div>

                            </template>

                            <template v-else-if="uploadCancellationToken && uploadPercentage < 100">

                                <div class="tw-rounded tw-bg-gray-200 tw-my-2">
                                    <div class="tw-rounded tw-bg-green-500 tw-h-2"
                                         :style="{ width: uploadPercentage + '%' }"></div>
                                </div>

                                Bezig met uploaden...<br/>
                                <span class="tw-text-xs tw-text-gray-500">Klik om te annuleren.</span>

                            </template>

                            <template v-else-if="uploadCancellationToken && uploadPercentage == 100">

                                <i class="fa fa-circle-notch tw-text-2xl fa-spin"></i>
                                <div class="mt-2">
                                    Bezig met optimaliseren..<br/>
                                    <span class="tw-text-xs tw-text-gray-500">Klik om te annuleren.</span>
                                </div>

                            </template>

                            <template v-else>

                                <i class="fa fa-upload tw-text-2xl"></i>
                                <div class="tw-mt-2">
                                    Sleep hier een afbeelding in<br/>
                                    of klik hier.
                                </div>

                            </template>

                        </div>

                        <input class="tw-hidden" type="file" @change="onChange">

                    </label>

                    <button
                        v-if="!uploadCancellationToken && hasMedia"
                        class="btn btn-outline-danger btn-sm tw-rounded-full tw-px-4 tw-py-2 tw-mt-3"
                        @click="removeImage">
                        <i class="fa fa-trash mr-1"></i> Verwijder
                    </button>

                </div>

                <div v-if="editorComponent.editableData.linkable" class="tw-mt-6">

                    <!-- TODO Critical: Review text -->
                    <div class="tw-text-gray-600 label">Verwijs naar pagina?</div>
                    <small class="tw-block tw-mb-3 tw-text-gray-500 label tw-text-muted">Kies een eventuele pagina
                        waarnaar de bezoeker wordt geleid na het klikken op de afbeelding.</small>

                    <website-page-selector
                        ref="websitePageSelector"
                        :value="options.linkWebsitePageId"
                        :language="'nl' /** TODO **/"
                        @update:value="setLink($event)"/>

                    <button class="btn btn-sm btn-success px-3 mt-3 save" @click="onSaveClick" :disabled="!linkChanged">
                        Opslaan
                    </button>

                </div>

            </div>

            <TextInput
                label="Alt tekst"
                instruction="Deze tekst wordt getoond als een afbeelding niet zichtbaar/beschikbaar is."
                placeholder="Beschrijvende tekst over de afbeelding."
                :options="options"
                options-property="altText"
                @valueChange="save()"/>

            <SwitchInput
                label="Hoe de foto tonen?"
                instruction="Wanneer deze niet past, op bijvoorbeeld een kleiner schermformaat."
                :options="options"
                options-property="imageDisplay"
                :items="[
                        { value: 'crop', label: 'Uitsnijden (croppen)'},
                        { value: 'scale', label: 'Schalen'},
                    ]"
                defaultValue="crop"
                @valueChange="save()"/>

            <SwitchInput
                label="Kunnen inzoomen?"
                instruction="Wanneer men op de foto klikt, opent de foto in het groot."
                :options="options"
                options-property="zoomable"
                :items="[
                        { value: 0, label: 'Nee'},
                        { value: 1, label: 'Ja'},
                    ]"
                :defaultValue="0"
                @valueChange="save()"/>

        </div>

        <avatar-cropper
            v-if="dropzoneRefPresent && hasCrop"
            ref="avatarCropper"
            :uploadHandler="cropUploadHandler"
            :labels="{
                    submit: 'Gereed',
                    cancel: 'Annuleer',
                }"
            :trigger="$refs['dropzone']"
            :outputOptions="editorComponent.editableData.outputOptions"
            :cropperOptions="cropperOptions"/>

    </FloatingPanel>

</template>

<script>

import axios from '@galaxy/js/common/AxiosBuilder';
import Swal from 'sweetalert2';
import AvatarCropper from "vue-avatar-cropper";

import AbstractPanel from "../../../../../../../js/common/canvas/editor/canvas/document/editors/AbstractPanel";
import FloatingPanel
    from "../../../../../../../js/common/canvas/editor/canvas/document/editors/panels/FloatingPanel";
import WebsitePageSelector from "../../../../../../../js/habitats/admin/common/WebsitePageSelector";
import SwitchInput from "../../../../../../../js/common/canvas/editor/canvas/document/editors/inputs/SwitchInput";
import TextInput from "@galaxy/js/common/canvas/editor/canvas/document/editors/inputs/TextInput";

export default {

    extends: AbstractPanel,

    components: {
        TextInput,
        SwitchInput,
        AvatarCropper,
        FloatingPanel,
        WebsitePageSelector,
    },

    data() {
        return {
            uploadCancellationToken: false,
            uploadPercentage: null,
            uploadSuccess: false,
            linkChanged: false,
            dropzoneRefPresent: false,
        };
    },

    mounted() {
        // @ugly Otherwise avatar croppen element wont find dropzone ref.
        this.dropzoneRefPresent = true;
    },

    methods: {

        allowDrop(event) {
            event.preventDefault();
        },

        async onDrop(event) {
            // Stop event.
            event.stopPropagation();
            event.preventDefault();

            //
            //this.onFile(event.dataTransfer.files[0]);

            // Get file.
            let file = _.first(event.dataTransfer?.files || []);
            if (!file)
                return;

            // Delegate
            if (this.outputOptions)
                await this.onCropNewFile(file);
            else
                await this.onFile(file);
        },

        async onCropNewFile(file) {
            let avatarCropper = this.$refs['avatarCropper'];
            // @note Taken from avatar cropper itself.
            let reader = new FileReader()
            reader.onload = e => {
                avatarCropper.dataUrl = e.target.result
            }
            reader.readAsDataURL(file)
            avatarCropper.filename = file.name || 'unknown'
            avatarCropper.mimeType = avatarCropper.mimeType || file.type
            avatarCropper.$emit('changed', file, reader)
        },

        async onChange(event) {
            await this.upload(event.target.files[0]);
        },

        async onFile(file) {
            await this.upload(file);
        },

        async upload(file) {
            // Abort due to no file selected?
            if (!file || !file.type)
                return;

            // Notify user due to not an image?
            if (!file.type.match('image.*'))
                await Swal.fire({
                    title: 'Ongeldig bestand',
                    text: 'Dit bestand is geen afbeelding. Enkel afbeeldingen zijn toegestaan!',
                    type: 'warning',
                });

            // Invalidate state due to valid upload?
            else if (await this.uploadToServer(file, file.name))
                this.invalidateState();
        },

        cropUploadHandler(resp) {
            // Crop
            this.$refs['avatarCropper'].cropper.getCroppedCanvas(this.outputOptions).toBlob(async blob => {
                // Get filename.
                let filename = this.$refs['avatarCropper'].filename;

                // Refresh due to uploaded successfully?
                if (await this.uploadToServer(blob, filename))
                    this.invalidateState();
            }, this.outputOptions?.mime || 'image/jpeg');
        },

        async uploadToServer(file, filename) {
            // Init form data.
            var formData = new FormData();

            formData.append('_method', 'PUT');
            formData.append('image', file, filename);

            // Init cancellation token.
            this.uploadCancellationToken = axios.CancelToken.source();

            // Reset upload percentage.
            this.uploadPercentage = 0;

            try {
                // Request
                await axios.post(this.adminUrlPrefix + '/' + this.canvasBlock.id, formData, {
                    headers: {
                        'content-type': 'multipart/form-data'
                    },
                    cancelToken: this.uploadCancellationToken.token,
                    onUploadProgress: progressEvent => {
                        // Calculate and set upload percentage.
                        this.uploadPercentage = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                    },
                });

                // Mark as successfully uploaded.
                this.uploadSuccess = true;

                //
                return true;
            } catch (e) {
                // Is cancel?
                if (axios.isCancel(e))
                    return false;

                // Notify user.
                this.$toasted.show('<i class="far fa-exclamation-triangle"></i> Fout bij verwerken afbeelding. Probeer het (later) opnieuw.', {type: 'error'});

                //
                throw e;
            } finally {
                // Mark as done.
                this.uploadCancellationToken = null;
                this.uploadPercentage = null;
            }
        },

        cancelUpload(event) {
            // Stop event to prevent opening file chooser.
            event.stopPropagation();
            event.preventDefault();

            // Cancel
            this.uploadCancellationToken.cancel();
            this.uploadCancellationToken = null;
        },

        async removeImage() {
            // Abort due to user does not confirm?
            const {value: confirmed} = await Swal.fire({
                title: 'Weet u het zeker?',
                text: 'Weet u zeker dat u deze wenst te verwijderen?',
                type: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Ja, verwijder',
                confirmButtonColor: '#f62d51',
                cancelButtonText: 'Annuleer',
            });

            if (!confirmed)
                return;

            // Remove media.
            await axios.delete(this.adminUrlPrefix + '/' + this.canvasBlock.id);

            //
            this.invalidateState();
        },

        invalidateState() {
            // Rerender
            this.$pageComponent().state.invalidate();
        },

        setLink(websitePageId) {
            // Set
            this.$set(this.options, 'linkWebsitePageId', websitePageId);

            //
            this.linkChanged = true;
        },

        onSaveClick() {
            this.save();

            this.linkChanged = false;
        },

        setImageDisplay(value) {
            // Set
            this.$set(this.options, 'imageDisplay', value);

            //
            this.save();
        },

    },

    computed: {

        // Not reactive.
        hasMedia() {
            return this.editorComponent.hasMedia;
        },

        hasCrop() {
            return !!this.outputOptions;
        },

        outputOptions() {
            return this.editorComponent.editableData.outputOptions;
        },

        cropperOptions() {
            return {
                aspectRatio: this.outputOptions.width / this.outputOptions.height,
                autoCropArea: 1,
                viewMode: 1,
                movable: false,
                zoomable: true,
            };
        },

    },

}

</script>

<style lang="scss" scoped>

.galaxy\:\:common-canvas-blocks-utils-image-BlockPanel {

    .dropzone {
        border-width: 1px;

        &.success {
            border-color:     #28a74557;
            border-style:     solid;
            background-color: #fbfffc;
        }
    }

}

</style>
